import { logger, AnalyticsModuleBootstrapError } from "../../debug";
import type { QuantumMetricAPI } from "./types";

/**
 * Check if Quantum Metric is ready
 */
export function isQuantumMetricReady(): boolean {
  const api = window.QuantumMetricAPI;
  if (!api) {
    return false;
  }
  if (typeof api.isReady === "function") {
    return api.isReady();
  }
  // Assume ready if API exists
  return true;
}

/**
 * Wait for Quantum Metric API to be available (with timeout)
 */
export async function waitForQuantumMetric(
  timeoutMs: number = 5000
): Promise<QuantumMetricAPI | null> {
  const startTime = Date.now();

  return new Promise((resolve) => {
    const checkInterval = setInterval(() => {
      const api = window.QuantumMetricAPI;
      if (api) {
        clearInterval(checkInterval);
        resolve(api);
        return;
      }

      if (Date.now() - startTime > timeoutMs) {
        clearInterval(checkInterval);
        logger.warn("Quantum Metric API did not initialize within timeout");
        resolve(null);
      }
    }, 100);
  });
}

/**
 * Track an event in Quantum Metric
 */
export function trackQuantumMetricEvent(
  eventName: string,
  eventData?: Record<string, unknown>
): void {
  const api = window.QuantumMetricAPI;
  if (!api) {
    logger.debug("Quantum Metric API not ready for event tracking", {
      eventName,
    });
    return;
  }
  try {
    api.trackEvent(eventName, eventData);
  } catch (error) {
    logger.warn(
      new AnalyticsModuleBootstrapError(
        "Failed to track Quantum Metric event",
        { cause: error }
      )
    );
  }
}

/**
 * Set Quantum Metric consent state
 */
export function setQuantumMetricConsentState(consented: boolean): void {
  const api = window.QuantumMetricAPI;
  if (!api) {
    logger.debug("Quantum Metric API not ready for consent update", {
      consented,
    });
    return;
  }
  try {
    if (consented) {
      api.optInUser();
    } else {
      api.optOutUser();
    }
  } catch (error) {
    logger.warn(
      new AnalyticsModuleBootstrapError(
        "Failed to set Quantum Metric consent state",
        { cause: error }
      )
    );
  }
}
